import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Loader2, Save } from "lucide-react";
import { getSettings, updateSettings } from "../../../services/dataService";
import { PageHeader } from "../../PageHeader";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { formatApiErrorDetail } from "../../../lib/api";
import { toast } from "sonner";

const FIELDS = [
  { name: "org_name", label: "Organisation Name" },
  { name: "registration_number", label: "Registration Number" },
  { name: "vat_number", label: "VAT Number" },
  { name: "contact_email", label: "Contact Email", type: "email" },
  { name: "contact_phone", label: "Contact Phone" },
  { name: "address", label: "Physical Address" },
];

export function OrganizationSettings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["settings", "organization"], queryFn: () => getSettings("organization") });
  const [form, setForm] = useState(null);

  useEffect(() => { if (data) setForm(data); }, [data]);

  const mut = useMutation({
    mutationFn: (payload) => updateSettings("organization", payload),
    onSuccess: (res) => { qc.setQueryData(["settings", "organization"], res); setForm(res); toast.success("Organization settings saved"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"),
  });

  if (isLoading || !form) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div>
      <PageHeader title="Organization Settings" icon={Building2} description="Your organisation's legal and contact details used on batch submissions." />
      <div className="max-w-2xl rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm space-y-5" data-testid="organization-form">
        {FIELDS.map((f) => (
          <div className="space-y-2" key={f.name}>
            <Label>{f.label}</Label>
            <Input type={f.type || "text"} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`org-${f.name}`} />
          </div>
        ))}
        <Button onClick={() => mut.mutate(form)} disabled={mut.isPending} className="bg-slate-900 hover:bg-slate-800 dark:bg-blue-600 text-white font-semibold" data-testid="org-save">
          {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Save Organization</>)}
        </Button>
      </div>
    </div>
  );
}
